import { Check, Minus, X } from "lucide-react";
import { FadeInUp } from "@/components/ui/motion";

const comparisonData = [
  {
    feature: "Learn and build quickly",
    makeplan: { status: "good" as const, note: "Visual builder" },
    noCode: { status: "good" as const, note: "Simple UI" },
    technical: { status: "medium" as const, note: "Code required" },
  },
  {
    feature: "See what happened when it fails",
    makeplan: { status: "good" as const, note: "Full logs + replay" },
    noCode: { status: "bad" as const, note: "Limited visibility" },
    technical: { status: "medium" as const, note: "DIY logging" },
  },
  {
    feature: "Handle failures gracefully",
    makeplan: { status: "good" as const, note: "Built-in retries" },
    noCode: { status: "medium" as const, note: "Basic retry" },
    technical: { status: "good" as const, note: "Full control" },
  },
  {
    feature: "Predictable costs at scale",
    makeplan: { status: "good" as const, note: "Transparent" },
    noCode: { status: "bad" as const, note: "Per-task pricing" },
    technical: { status: "good" as const, note: "Self-hosted" },
  },
];

function StatusIcon({ status }: { status: "good" | "medium" | "bad" }) {
  switch (status) {
    case "good":
      return <Check className="w-4 h-4 text-success" />;
    case "medium":
      return <Minus className="w-4 h-4 text-warning" />;
    case "bad":
      return <X className="w-4 h-4 text-destructive/70" />;
  }
}

export function ProblemSolution() {
  return (
    <section id="product" className="py-16 lg:py-24 section-elevated relative">
      {/* Decorative pattern */}
      <div className="absolute inset-0 pattern-dots opacity-30 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-4xl mx-auto">
          <FadeInUp className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4 tracking-tight">
              The automation tradeoff-
              <span className="gradient-text">solved</span>
            </h2>
            <p className="text-lg text-muted-foreground/90 max-w-2xl mx-auto">
              No-code tools are easy but opaque. Developer tools give control
              but slow you down. Makeplans gives you both.
            </p>
          </FadeInUp>

          <FadeInUp delay={0.2}>
            <div className="glass rounded-xl overflow-hidden shadow-lg">
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b border-border/50 bg-muted/50">
                      <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-medium text-muted-foreground w-1/4"></th>
                      <th className="text-center py-3 sm:py-4 px-2 sm:px-4">
                        <span className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1 sm:py-1.5 rounded-full bg-primary/20 text-primary text-[10px] sm:text-xs font-semibold border border-primary/30">
                          Makeplans
                        </span>
                      </th>
                      <th className="text-center py-3 sm:py-4 px-2 sm:px-4 font-medium text-muted-foreground text-[10px] sm:text-xs">
                        No-code tools
                      </th>
                      <th className="text-center py-3 sm:py-4 px-2 sm:px-4 font-medium text-muted-foreground text-[10px] sm:text-xs">
                        Node-based
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row, index) => (
                      <tr
                        key={index}
                        className="border-b border-border/30 last:border-0 hover:bg-muted/30 transition-colors"
                      >
                        <td className="py-4 sm:py-5 px-4 sm:px-6 text-foreground font-medium">
                          {row.feature}
                        </td>
                        <td className="py-4 sm:py-5 px-2 sm:px-4">
                          <div className="flex flex-col items-center gap-1 sm:gap-1.5">
                            <StatusIcon status={row.makeplan.status} />
                            <span className="text-[10px] sm:text-xs text-muted-foreground text-center">
                              {row.makeplan.note}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 sm:py-5 px-2 sm:px-4">
                          <div className="flex flex-col items-center gap-1 sm:gap-1.5">
                            <StatusIcon status={row.noCode.status} />
                            <span className="text-[10px] sm:text-xs text-muted-foreground text-center">
                              {row.noCode.note}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 sm:py-5 px-2 sm:px-4">
                          <div className="flex flex-col items-center gap-1 sm:gap-1.5">
                            <StatusIcon status={row.technical.status} />
                            <span className="text-[10px] sm:text-xs text-muted-foreground text-center">
                              {row.technical.note}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </FadeInUp>
        </div>
      </div>
    </section>
  );
}
